/**
 * Mounted once near the QueryClientProvider. Connects the SSE client to the
 * React Query cache by the rules in `rules.ts`: each batch of `invalidate`
 * events refreshes the queries its targets name (throttled ones at most once
 * per window), `reconnect` refreshes everything a missed event could have
 * touched, and `unauthorized` sends the user to log in.
 *
 * In a hidden tab queries are only marked stale; `refetchOnWindowFocus`
 * refetches them when the tab comes back, so an idle tab costs nothing.
 */

import { type Query, type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { redirectToLogin } from "../api/client.ts";
import { useAuth } from "../contexts/AuthContext";
import { LIVE_TARGETS, fires, rulesFor, targetFor } from "./rules.ts";
import { sseClient, useRealtimeStatus } from "./sseClient";

// While the connection is down, `meta.live` queries refetch this often.
const FALLBACK_POLL_MS = 30_000;

const refetchType = () => (document.visibilityState === "hidden" ? "none" : "active");

function invalidateAll(queryClient: QueryClient, predicate: (q: Query) => boolean): void {
  void queryClient.invalidateQueries({ predicate, refetchType: refetchType() });
}

export function useSseInvalidations(): void {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const status = useRealtimeStatus();

  useEffect(() => {
    if (!user) {
      sseClient.disconnect();
      return;
    }

    // Per query hash: when it last refetched, and the trailing refetch waiting.
    const throttles = new Map<string, { last: number; timer?: ReturnType<typeof setTimeout> }>();
    const throttle = (query: Query, ms: number) => {
      const now = Date.now();
      const t = throttles.get(query.queryHash);
      if (!t || now - t.last >= ms) {
        throttles.set(query.queryHash, { last: now });
        invalidateAll(queryClient, (q) => q === query);
      } else if (!t.timer) {
        t.timer = setTimeout(() => {
          throttles.set(query.queryHash, { last: Date.now() });
          invalidateAll(queryClient, (q) => q.queryHash === query.queryHash);
        }, t.last + ms - now);
      }
    };

    const offInvalidate = sseClient.onInvalidate((events) => {
      const now = new Set<Query>();
      const later = new Map<Query, number>();
      for (const query of queryClient.getQueryCache().getAll()) {
        const key = query.queryKey;
        for (const evt of events) {
          const target = targetFor(rulesFor(evt.model), key);
          if (!target || !fires(target, key, evt.id)) continue;
          if (target.throttleMs) later.set(query, target.throttleMs);
          else now.add(query);
        }
      }
      for (const query of now) {
        later.delete(query);
        const t = throttles.get(query.queryHash);
        if (t) {
          clearTimeout(t.timer);
          throttles.set(query.queryHash, { last: Date.now() });
        }
      }
      if (now.size) invalidateAll(queryClient, (q) => now.has(q));
      for (const [query, ms] of later) throttle(query, ms);
    });

    const offReconnect = sseClient.onReconnect(() => {
      invalidateAll(queryClient, (q) => targetFor(LIVE_TARGETS, q.queryKey) !== undefined);
    });

    const offUnauthorized = sseClient.onUnauthorized(redirectToLogin);

    sseClient.connect();

    return () => {
      offInvalidate();
      offReconnect();
      offUnauthorized();
      for (const t of throttles.values()) clearTimeout(t.timer);
    };
  }, [user, queryClient]);

  // The fallback while the connection is down. Not a `refetchInterval` in the
  // query defaults: an observer only re-reads that after its own fetch, so a
  // drop between fetches would never start the polling.
  useEffect(() => {
    if (!user || status !== "down") return;
    const timer = setInterval(
      () => invalidateAll(queryClient, (q) => q.meta?.live === true),
      FALLBACK_POLL_MS,
    );
    return () => clearInterval(timer);
  }, [user, status, queryClient]);
}
